/**
 * Pure helpers for the "Import a statement" control on CashBook.jsx.
 *
 * Kept out of the .jsx file for the same reason every other rule on this
 * screen lives in lib/cashBookGrid.js and not in the component: node's own
 * test runner (`npm test` -> `node --test src/lib/*.test.js`) cannot parse
 * JSX, so anything decided here is the only part of the import feature this
 * repository can actually run a test against. The component itself is a thin
 * shell that calls these and renders what they return.
 *
 * NOTHING HERE TOUCHES MONEY ARITHMETIC. `api.js`'s header states the rule —
 * the browser never adds, subtracts or reformats a cents figure — and this
 * file only reads fields the server already formatted (`summary.openingBalance`
 * etc, both decimal strings) or counts that are plain small integers
 * (`rowsKept`, `rulesApplied`, ...), never a `*Cents` value.
 */

/**
 * The base64 payload out of a `FileReader.readAsDataURL` result.
 *
 * `readAsDataURL` yields `"data:<mime-type>;base64,<data>"`. Split on the
 * FIRST comma, not the last: a `;base64,` marker never itself contains a
 * comma, but taking `indexOf` rather than `lastIndexOf` is the deliberate
 * choice here in case a future browser or a future mime type ever put one in
 * the type portion — this must always keep everything after the marker, not
 * whatever happens to follow the last comma in the string.
 *
 * Pulled out on its own because a wrong split here corrupts every byte of the
 * uploaded file silently: the request still goes out, still has a body, and
 * the failure only surfaces two steps later as `NOT_AN_ABSA_EXPORT` — a
 * message that points at the file, not at the one line in the browser that
 * mangled it.
 */
export function base64FromDataUrl(dataUrl) {
  const s = String(dataUrl ?? '');
  const i = s.indexOf(',');
  if (i === -1) throw new Error('not a data URL: expected "data:...;base64,<data>"');
  return s.slice(i + 1);
}

/**
 * The sentence the task brief requires verbatim, shown when a statement's
 * running balance could not be checked line by line. `balanceVerified` is a
 * REAL fact about the file (`absaXlsx.js`'s `verifyBalanceChain`), not a
 * quality score — two of the three real ABSA exports this feature was built
 * against are like this, so this is an ordinary case the screen must handle
 * plainly, not an edge case to bury.
 */
export const UNVERIFIED_BALANCE_MESSAGE =
  'This statement carries no running balance, so completeness could not be verified.';

/**
 * True exactly when the honesty banner above must show.
 *
 * Written as `=== false`, not `!summary.balanceVerified`, and kept as its own
 * function rather than inlined into a JSX `{cond && ...}` so that distinction
 * has a test: `undefined` (a summary shape that changed, a field that failed
 * to arrive) must not silently read as "verified", but it must also not
 * trip the "definitely NOT verified" banner and claim a fact the response
 * never asserted. Both are wrong in different ways, and only the strict
 * comparison tells them apart.
 */
export function statementIsUnverified(summary) {
  return summary?.balanceVerified === false;
}

/**
 * The period line for the preview, or the honest sentence for the one real
 * case `cashBookImport.js`'s `summarise` documents on the API side: every row
 * in the file was a dropped zero, so there is no first or last date to show.
 */
export function formatImportDateRange(summary) {
  if (!summary?.fromDate || !summary?.toDate) return 'No rows in this statement';
  if (summary.fromDate === summary.toDate) return summary.fromDate;
  return `${summary.fromDate} – ${summary.toDate}`;
}

/**
 * The three-tier coding split, summed, for the "how much of this was a
 * guess" line the brief calls the point of the preview screen.
 *
 * `total` is CHECKED HERE against `summary.rowsKept`, not just computed and
 * left for a reader to trust. Round 1 review (2026-09-19) found this
 * comment previously claimed that check existed when it did not — `total`
 * was returned but never consumed by the component or asserted by a test, so
 * a row falling under none of the three tiers (a fourth, forgotten case)
 * would have silently undercounted `rowsKept` with nothing anywhere to catch
 * it. `cashBookImport.test.js` now has a test asserting
 * `codingSplit(summary).total === summary.rowsKept` for a realistic summary,
 * proven load-bearing by breaking this function and watching it fail, and
 * the `console.warn` below is the runtime half of the same check: a dev
 * console warning, not a thrown error, so a legitimate future change to the
 * summary shape cannot crash this screen over a header comment's promise.
 */
/**
 * The one sentence the Pastel import preview must not let a reader miss.
 *
 * `vatRateOutliers` is `api/src/lib/cashBookImport.js`'s `summarisePastelRows`
 * count of lines whose implied VAT rate sits outside the tax code's statutory
 * 15% — imported with the client's own Exclusive/Tax figures unchanged
 * (`trustGivenAmounts`, same file), because the data is the client's and the
 * server refuses nothing here. But that count feeds VAT201, a statutory
 * return, so the ONE job of this function is to put a number in front of the
 * operator that cannot be skimmed past — not a bare stat tile among the
 * others, and not silence when it is zero (silence reads as "nobody checked"
 * exactly as easily as it reads as "there is nothing to see").
 *
 * Written as its own function, not inlined into JSX, for the same reason
 * `statementIsUnverified` is: the zero case and the non-zero case are two
 * different sentences, not one sentence with a number spliced in, and a test
 * can pin both wordings without a DOM.
 */
export function vatOutlierMessage(summary) {
  const n = summary?.vatRateOutliers ?? 0;
  if (n === 0) {
    return "Every line fits the tax code's statutory 15% VAT rate.";
  }
  return `${n} line${n === 1 ? '' : 's'} ${n === 1 ? 'has' : 'have'} an implied VAT rate outside `
    + "the tax code's statutory 15% — imported with the client's own figures unchanged. Check "
    + `${n === 1 ? 'it' : 'them'} before this batch feeds the VAT201.`;
}

/**
 * True exactly when the VAT rate check above must be shown as a warning
 * rather than a plain confirmation — kept as its own predicate (rather than
 * asking a component to test `summary.vatRateOutliers > 0` inline) so the
 * "what counts as an outlier" decision has exactly one home, the same
 * discipline `statementIsUnverified` follows for the balance banner.
 */
export function hasVatRateOutliers(summary) {
  return (summary?.vatRateOutliers ?? 0) > 0;
}

export function codingSplit(summary) {
  const rulesApplied = summary?.rulesApplied ?? 0;
  const historyApplied = summary?.historyApplied ?? 0;
  const uncoded = summary?.uncoded ?? 0;
  const total = rulesApplied + historyApplied + uncoded;
  if (typeof summary?.rowsKept === 'number' && total !== summary.rowsKept) {
    // eslint-disable-next-line no-console -- deliberate: see the doc comment above.
    console.warn(
      `cashBookImport.codingSplit: rulesApplied+historyApplied+uncoded (${total}) does not `
      + `match rowsKept (${summary.rowsKept}) — a row was coded under no tier, or under more `
      + 'than one.',
    );
  }
  return { rulesApplied, historyApplied, uncoded, total };
}

// ---------------------------------------------------------------------------
// Holding accounts for uncoded lines.
//
// A chart with no postable 7300 / 4900 (a Pastel-derived renumbering) cannot
// use the importer's by-code holding account, so the screen lets the
// bookkeeper name one for payments and one for receipts. The server checks
// them (`validateFallbackAccount`); this only builds the picker's options and
// the request keys. "" means Automatic: send nothing, keep the by-code lookup.
// ---------------------------------------------------------------------------

export const HOLDING_KEY_PREFIX = 'ironledger.cashBook.importHolding.v1.';

const NO_CHOICE = () => ({ payments: '', receipts: '' });

/** Postable, active, non-bank accounts — a bank account is never a holding account. */
export const holdingAccountOptions = (accounts = []) =>
  (accounts ?? []).filter((a) => a.is_postable && a.is_active && !a.is_bank);

/** The request keys for a choice; a blank selection contributes no key at all. */
export function holdingBody(choice) {
  const body = {};
  if (choice?.payments) body.fallbackAccountId = choice.payments;
  if (choice?.receipts) body.fallbackReceiptAccountId = choice.receipts;
  return body;
}

/**
 * The remembered choice for an entity, with any id no longer among the options
 * dropped to Automatic. Storage is a convenience only: blocked, absent or
 * corrupt storage yields Automatic and never throws.
 */
export function loadHoldingChoice(storage, entityId, accounts) {
  try {
    const raw = storage?.getItem(HOLDING_KEY_PREFIX + entityId);
    if (!raw) return NO_CHOICE();
    const saved = JSON.parse(raw);
    const ids = new Set(holdingAccountOptions(accounts).map((a) => a.id));
    const keep = (id) => (typeof id === 'string' && ids.has(id) ? id : '');
    return { payments: keep(saved?.payments), receipts: keep(saved?.receipts) };
  } catch { return NO_CHOICE(); }
}

export function saveHoldingChoice(storage, entityId, choice) {
  try {
    storage?.setItem(HOLDING_KEY_PREFIX + entityId, JSON.stringify({
      payments: choice?.payments ?? '', receipts: choice?.receipts ?? '',
    }));
  } catch { /* a screen preference; losing it costs nothing */ }
}
